import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// 15s ceiling on every request so a stalled backend never freezes the UI forever.
// The Paywalled loader also has a client-side escape hatch after 8s in case react-query
// is stuck between attempts — see /app/frontend/src/App.js.
export const api = axios.create({ baseURL: API_BASE, timeout: 15000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("edge_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// A 401 from EVERY endpoint used to force-logout, which caused legit customers
// to get randomly kicked to /auth mid-session (e.g. a transient video-stream
// 401, or a delayed webhook that briefly returned a stale user record).
//
// Fix: only force-logout when the token itself is invalid — signaled by a 401
// on /auth/me or /billing/status. For any other 401 (video streams, analyze
// calls, admin routes) we just bubble the error to the caller and let them
// handle it. Users stay logged in and retry on their own.
const AUTH_401_LOGOUT_PATHS = new Set([
  "/auth/me",
  "/billing/status",
]);

// Track consecutive auth failures — a single 401 (transient backend hiccup,
// mid-request restart, Cloudflare 502 wrapped as 401, etc.) NEVER kicks a
// user out. Only 3 consecutive 401s on auth-check endpoints within 30
// seconds forces a logout. Any 2xx response resets the counter.
let authFailStreak = 0;
let authFailStreakStartedAt = 0;

api.interceptors.response.use(
  (r) => {
    // Reset streak on any successful response — even one 2xx proves the
    // network + backend are alive and the token was accepted somewhere.
    if (r.status >= 200 && r.status < 300) {
      authFailStreak = 0;
      authFailStreakStartedAt = 0;
    }
    return r;
  },
  (err) => {
    if (err?.response?.status === 401) {
      const path = (err?.config?.url || "").split("?")[0].replace(/^\/+/, "/");
      const stripped = path.startsWith("/api") ? path.slice(4) : path;
      const isAuthCheck = AUTH_401_LOGOUT_PATHS.has(stripped);
      if (isAuthCheck) {
        const now = Date.now();
        // If the streak is stale (last 401 was >30s ago), start fresh.
        if (now - authFailStreakStartedAt > 30_000) {
          authFailStreak = 0;
          authFailStreakStartedAt = now;
        }
        authFailStreak++;
        // Only clear session after 3 consecutive 401s. A single 401 (network
        // blip, CF returning 401 wrapper for a 5xx, backend restart, etc.)
        // NEVER kicks the user out anymore.
        if (authFailStreak >= 3) {
          localStorage.removeItem("edge_token");
          try { localStorage.removeItem("edge_last_good_billing"); } catch { /* ignore */ }
          authFailStreak = 0;
          authFailStreakStartedAt = 0;
          if (!window.location.pathname.startsWith("/auth")) {
            window.location.href = "/auth";
          }
        }
      }
      // else: silently reject — caller can decide how to handle
    }
    return Promise.reject(err);
  }
);

export const fmtUSD = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const fmtPct = (n) => `${Number(n || 0).toFixed(2)}%`;

export const fmtHold = (mins) => {
  const m = Number(mins || 0);
  if (m <= 0) return "—";
  if (m < 1) return `${Math.round(m * 60)}s`;
  if (m < 60) return `${m.toFixed(1)}m`;
  const h = Math.floor(m / 60);
  const rest = Math.round(m - h * 60);
  return rest ? `${h}h ${rest}m` : `${h}h`;
};
